import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/PasswordInput";
import { Sparkles, HandCoins } from "lucide-react";
import { toast } from "sonner";

const initial = { name: "", phone: "", address: "", password: "", invited_by_name: "" };

const CONTRACT_TEXT = `CONTRATO DE PROMOTOR INDEPENDIENTE

Entre OFERTAS (en adelante "la EMPRESA") y el suscrito (en adelante "el PROMOTOR"), se celebra el presente contrato de prestación de servicios de promoción bajo las siguientes cláusulas:

1. NATURALEZA DE LA RELACIÓN. El PROMOTOR reconoce no ser un empleado directo de la EMPRESA, por lo tanto deslinda a la misma de las responsabilidades laborales de las relaciones obrero/patronales que pudieran derivarse de la presente relación comercial.

2. ACTIVIDAD INDEPENDIENTE. El PROMOTOR reconoce que su actividad comercial es clasificada como persona física con actividad independiente, por lo tanto sus ingresos por las ventas que realice serán honorarios, por los cuales deberá presentar una factura con todos los requisitos fiscales y legales que corresponda conforme a la legislación mexicana vigente.

3. COMISIONES. El PROMOTOR recibirá una comisión de $100 MXN por cada mes en que un comercio referido por él realice su pago de suscripción, sin garantía de exclusividad territorial.

4. VIGENCIA. El presente contrato estará vigente mientras el PROMOTOR mantenga su cuenta activa en la plataforma y podrá darse por terminado en cualquier momento sin responsabilidad para ninguna de las partes.`;

export default function PromoterRegister() {
  const { registerPromoter } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!contractAccepted) return toast.error("Debes aceptar el contrato para continuar");
    if (form.password.length < 4) return toast.error("La contraseña debe tener al menos 4 caracteres");
    setBusy(true);
    try {
      await registerPromoter({ ...form, contract_accepted: true });
      toast.success("¡Listo! Cuenta de promotor creada.");
      navigate("/promotor");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrarte");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="overline text-[hsl(var(--primary))]">Únete como promotor</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Gana $100 MXN por cada comercio que afilies</h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-xl">
          Cada mes que tu comercio afiliado pague su cuota de $199, tú ganas $100 MXN. Sin tope, sin letras chiquitas.
        </p>

        <form onSubmit={submit} className="mt-6 bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-5" data-testid="promoter-register-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Nombre completo *</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Juan Pérez" data-testid="promoter-reg-name" />
            </div>
            <div>
              <Label className="text-sm">Teléfono celular *</Label>
              <Input
                required
                value={form.phone}
                onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="5512345678"
                inputMode="numeric"
                data-testid="promoter-reg-phone"
              />
              {form.phone.length > 0 && form.phone.length < 10 && (
                <p className="text-[11px] text-amber-600 mt-1">{form.phone.length}/10 dígitos — faltan {10 - form.phone.length}</p>
              )}
              {form.phone.length === 10 && (
                <p className="text-[11px] text-green-600 mt-1">✓ Número completo</p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-sm">Domicilio *</Label>
            <Input required value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Calle, número, colonia, ciudad" data-testid="promoter-reg-address" />
          </div>
          <div>
            <Label className="text-sm">¿Qué promotor te invitó? (opcional)</Label>
            <Input value={form.invited_by_name} onChange={(e) => set("invited_by_name", e.target.value)} placeholder="Nombre del promotor que te invitó" data-testid="promoter-reg-invited" />
          </div>
          <div>
            <Label className="text-sm">Contraseña *</Label>
            <PasswordInput
              required
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="Mín. 4 caracteres"
              data-testid="promoter-reg-password"
            />
            {form.password.length > 0 && form.password.length < 4 && (
              <p className="text-[11px] text-amber-600 mt-1">Faltan {4 - form.password.length} caracteres más</p>
            )}
            {form.password.length >= 4 && (
              <p className="text-[11px] text-green-600 mt-1">✓ Contraseña válida</p>
            )}
            {form.password.length === 0 && (
              <div className="text-[11px] text-muted-foreground mt-1">Una contraseña sencilla está bien. Solo te servirá para entrar a tu panel.</div>
            )}
          </div>

          <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg p-3 flex items-start gap-2 text-xs">
            <HandCoins className="w-4 h-4 mt-0.5 text-[#7C3AED] shrink-0" />
            <span className="text-zinc-700">
              <strong>Cómo funciona:</strong> Cuando un comercio se registra y pone tu nombre en el campo "promotor", queda ligado a tu cuenta. Cada mes que pague sus $199, automáticamente acumulas $100 de comisión que podrás cobrar al admin.
            </span>
          </div>

          {/* Contrato */}
          <div className="space-y-3">
            <div
              className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 h-48 overflow-y-auto text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap font-mono"
              data-testid="promoter-contract-text"
            >
              {CONTRACT_TEXT}
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="promoter-contract-label">
              <input
                type="checkbox"
                checked={contractAccepted}
                onChange={(e) => setContractAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))] cursor-pointer shrink-0"
                data-testid="promoter-contract-checkbox"
              />
              <span className="text-sm font-semibold text-zinc-800 select-none">
                ACEPTO EL CONTRATO — He leído y acepto los términos del contrato de promotor independiente.
              </span>
            </label>
          </div>

          <Button type="submit" disabled={busy || !contractAccepted} className="w-full btn-primary rounded-full py-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="promoter-reg-submit">
            <Sparkles className="w-4 h-4 mr-2" />
            {busy ? "Creando cuenta…" : "Crear mi cuenta de promotor"}
          </Button>
          <div className="text-sm text-center text-muted-foreground">
            ¿Ya tienes cuenta? <Link to="/login-promotor" className="text-[hsl(var(--primary))] font-medium">Inicia sesión</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
